import axios from "axios";
import { API } from "src/Funcs/APIFuncs/APIFunc";
import { getAccessToken } from "./authStore";

const ServerToken = import.meta.env.VITE_SERVER_TOKEN;

/**
 * Set by BoardGate while an anonymous (not logged-in) visitor is viewing a
 * public board. Read by the axios interceptor so 401 noise (tokenless calls
 * to member-only endpoints) neither toasts nor redirects the viewer.
 */
let anonymousPublicBoard = false;

export function setAnonymousPublicBoard(value) {
  anonymousPublicBoard = Boolean(value);
}

export function isAnonymousPublicBoard() {
  return anonymousPublicBoard;
}

/**
 * Probe board access before mounting /Board — keeps the "load then 401" flash
 * off the page.
 *
 * Uses GET /board/GetBoardGeneralData/:boardId, whose backend middlewares
 * already encode the access model:
 *   - member (or any GET on a public board)   -> 200
 *   - anonymous on private board              -> 401
 *   - authenticated non-member on private     -> 401
 *   - board does not exist                    -> 404
 * so the outcome is decided by status code + local session state — no new
 * backend endpoint needed.
 *
 * Intentionally plain axios (not axiosInstance): the probe must not trigger
 * the response interceptor's session-expiry redirect/toast.
 *
 * @returns {Promise<{outcome: "allow"|"deny"|"notfound"|"error", publicBoard: boolean}>}
 */
export async function probeBoardAccess(boardId) {
  try {
    const headers = { "x-auth-token-server": ServerToken || "" };
    const token = getAccessToken();
    if (token) headers["x-auth-token"] = token;

    const { data } = await axios.get(
      `${API}board/GetBoardGeneralData/${boardId}`,
      { headers },
    );

    return {
      outcome: "allow",
      publicBoard: data?.PrivacyMood === "Public",
    };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return { outcome: "notfound", publicBoard: false };
    if (status === 401 || status === 403) {
      return { outcome: "deny", publicBoard: false };
    }
    return { outcome: "error", publicBoard: false };
  }
}
